import React from 'react';
import { FaDiscord } from 'react-icons/fa';
import { useWallet } from '../hooks/useWallet';
import { useAccess } from '../hooks/useAccess';

const katbotUrl = new URL('../assets/katbot_big_2.png', import.meta.url).href;
const DISCORD_URL = 'https://discord.gg/asTcxUTzRv';

const Hero = ({ config }) => {
  const { login, isLoading } = useWallet();
  // The only marketing CTA wired to authentication (KAT-230). When wallet auth
  // is closed the button stays — the hero composition needs two side by side —
  // but it points somewhere that still works.
  const { login: loginOpen } = useAccess();

  return (
    <section aria-label="Hero" className="relative w-full flex items-center overflow-hidden">
      {/* Background glow blobs */}
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-purple-600/20 via-cyan-500/10 to-transparent blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-cyan-600/15 via-blue-500/10 to-transparent blur-3xl pointer-events-none" />

      <div className="relative px-2 py-10 lg:py-10 w-full lg:mr-8">
        <div className="flex flex-col lg:flex-row lg:gap-2 items-center justify-center max-w-5xl mx-auto">
          {/* Left: Illustration */}
          <div className="flex justify-center flex-1 mb-8 lg:mb-0">
            <img
              src={katbotUrl}
              alt="Katbot Illustration"
              className="w-full max-w-sm lg:max-w-md h-auto object-contain drop-shadow-[0_0_40px_rgba(34,211,238,0.15)]"
            />
          </div>

          {/* Right: Text */}
          <div className="text-center lg:text-left flex-1 lg:mr-5 m-2">
            <p className="uppercase tracking-widest text-cyan-400 text-sm font-mono mb-2">
              AI-Powered Trading Intelligence
            </p>
            <h1 className="bg-gradient-to-r from-green-300 via-blue-500 to-purple-600 bg-clip-text text-transparent text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
              Your AI Edge on Hyperliquid
            </h1>
            <p className="text-lg text-gray-300 mt-3 max-w-lg mx-auto lg:mx-0 lg:text-xl">
              Katbot doesn't just execute trades — it <span className="text-cyan-300 font-semibold">finds them</span>. An agentic AI platform purpose-built for <span className="text-orange-300 font-semibold">leveraged perpetuals on Hyperliquid</span>, Katbot scans the market, generates <span className="text-purple-300 font-semibold">high-conviction recommendations</span>, and manages risk so you can trade with <span className="text-green-300 font-semibold">confidence, not guesswork</span>.
            </p>
            <p className="text-base text-gray-400 mt-3 max-w-lg mx-auto lg:mx-0">
              Deploy <span className="text-cyan-300 font-semibold">AI agents</span> that learn, adapt, and collaborate on your portfolios — combining <span className="text-purple-300 font-semibold">on-chain analytics</span> with <span className="text-green-300 font-semibold">disciplined position sizing</span> and automated execution. Whether you're running a single strategy or <span className="text-orange-300 font-semibold">orchestrating multiple agents</span> across positions, Katbot brings <span className="text-cyan-400 font-semibold">institutional-grade intelligence</span> to your Hyperliquid trades.
            </p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mt-5">
              {loginOpen ? (
                <>
                  <button
                    onClick={login}
                    disabled={isLoading}
                    className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-green-400 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isLoading ? 'Connecting…' : 'Early Access'}
                  </button>
                  <a
                    href={config.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center border border-white/20 text-white px-6 py-2.5 rounded-xl font-semibold hover:border-white/40 hover:bg-white/5 transition-all duration-200 no-underline"
                  >
                    Read the Docs
                  </a>
                </>
              ) : (
                <>
                  <a
                    href={config.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-green-400 hover:to-emerald-500 transition-all duration-200 no-underline"
                  >
                    Read the Docs
                  </a>
                  <a
                    href={DISCORD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-2.5 rounded-xl font-semibold hover:border-white/40 hover:bg-white/5 transition-all duration-200 no-underline"
                  >
                    <FaDiscord className="text-lg" />
                    Join Discord
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
